import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, RefreshCw, Box, Gamepad2, ArrowRight } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is a Loop?",
        icon: "🔄",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <h3 className="font-bold text-xl text-orange-800 mb-3">Computers Love Repetition</h3>
                    <p className="text-slate-700 mb-4">
                        A <strong>loop</strong> runs the same code multiple times. Instead of writing
                        the same line 100 times, you write it once and tell the computer "repeat this 100 times."
                    </p>
                    <div className="font-mono text-sm bg-slate-800 text-white p-4 rounded-lg">
                        <div><span className="text-purple-400">for</span> (</div>
                        <div className="ml-4"><span className="text-blue-400">let i = 0</span>; <span className="text-slate-400">// Start at 0</span></div>
                        <div className="ml-4"><span className="text-green-400">i {"<"} 10</span>;   <span className="text-slate-400">// Stop at 10</span></div>
                        <div className="ml-4"><span className="text-yellow-400">i++</span>       <span className="text-slate-400">// Add 1 each time</span></div>
                        <div>) {"{"} ... {"}"}</div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "What is an Array?",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-2xl border-2 border-slate-200">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">Lists of Things</h3>
                    <p className="text-slate-700 mb-4">
                        An <strong>array</strong> is a list that stores multiple items.
                        Think of it as a row of boxes, each with a number (index).
                    </p>
                    <div className="font-mono text-sm bg-white p-4 rounded-lg border border-slate-300">
                        <div><span className="text-purple-400">const</span> fruits = [<span className="text-green-600">"🍎"</span>, <span className="text-green-600">"🍌"</span>, <span className="text-green-600">"🍇"</span>];</div>
                        <div className="text-slate-400 mt-2">// Index: &nbsp;&nbsp;&nbsp;0 &nbsp;&nbsp;&nbsp;&nbsp;1 &nbsp;&nbsp;&nbsp;&nbsp;2</div>
                    </div>
                    <div className="mt-4 p-4 bg-orange-50 text-orange-800 rounded-xl text-sm">
                        Access items by index: <code className="font-bold">fruits[0]</code> → "🍎"
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Types of Loops",
        icon: "🔁",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg">Different loops for different jobs:</p>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <h3 className="font-bold text-orange-600 mb-2">for loop</h3>
                        <p className="text-slate-600 text-sm">Best when you know <strong>HOW MANY</strong> times to repeat.</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <h3 className="font-bold text-blue-600 mb-2">while loop</h3>
                        <p className="text-slate-600 text-sm">Repeat <strong>WHILE</strong> a condition is true.</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <h3 className="font-bold text-purple-600 mb-2">forEach</h3>
                        <p className="text-slate-600 text-sm">Loop through <strong>EACH</strong> item in an array.</p>
                    </div>
                </div>
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-bold text-green-800 mb-2">🎮 Ready to Loop?</h4>
                    <p className="text-green-700 text-sm">
                        In the activity, you'll configure a loop to count bananas!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const LoopGame = ({ onComplete }: { onComplete: () => void }) => {
    const [laps, setLaps] = useState(5);
    const [currentLap, setCurrentLap] = useState(-1);
    const [isRunning, setIsRunning] = useState(false);
    const [bananas, setBananas] = useState<string[]>([]);
    const [speed] = useState(1000); // ms per lap

    useEffect(() => {
        let interval: any;
        if (isRunning) {
            if (currentLap < laps) {
                interval = setTimeout(() => {
                    setCurrentLap(prev => prev + 1);
                    if (currentLap >= 0) {
                        setBananas(prev => [...prev, '🍌']);
                    }
                }, speed);
            } else {
                setIsRunning(false);
                onComplete();
            }
        }
        return () => clearTimeout(interval);
    }, [isRunning, currentLap, laps, speed, onComplete]);

    const startRun = () => {
        setBananas([]);
        setCurrentLap(-1);
        setIsRunning(true);
    };

    const reset = () => {
        setIsRunning(false);
        setCurrentLap(-1);
        setBananas([]);
    };

    return (
        <div className="text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start w-full">

                {/* LEFT: Controls & Code */}
                <div className="space-y-6">
                    {/* Controls */}
                    <div className="bg-slate-900/80 backdrop-blur-xl p-8 rounded-3xl shadow-xl border border-white/10">
                        <label className="block text-slate-400 font-bold mb-4 uppercase text-xs tracking-wider">Number of Laps (Iterations)</label>
                        <div className="flex items-center gap-6 mb-8">
                            <input
                                type="range"
                                min="1" max="20"
                                value={laps}
                                onChange={(e) => setLaps(Number(e.target.value))}
                                disabled={isRunning}
                                className="w-full accent-orange-500 h-3 bg-slate-700 rounded-full appearance-none cursor-pointer hover:bg-slate-600 transition-colors"
                            />
                            <div className="text-4xl font-black text-orange-400 w-16 text-center tabular-nums">{laps}</div>
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={startRun}
                                disabled={isRunning}
                                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-xl shadow-lg shadow-green-900/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
                            >
                                {isRunning ? (
                                    <><RefreshCw className="animate-spin" /> Running...</>
                                ) : (
                                    <><Play fill="currentColor" size={20} /> Start Loop</>
                                )}
                            </button>
                            <button
                                onClick={reset}
                                className="bg-slate-800 text-slate-400 py-4 px-6 rounded-xl font-bold hover:bg-slate-700 hover:text-white active:scale-95 transition-all border border-slate-700"
                            >
                                <RefreshCw size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Code Visualizer */}
                    <div className="bg-black/50 backdrop-blur-md rounded-2xl p-6 shadow-xl text-slate-300 font-mono text-sm border-l-4 border-orange-500 overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <CodeIcon size={64} />
                        </div>
                        <div className="mb-2">
                            <span className="text-purple-400">const</span> <span className="text-orange-300">bananas</span> = [];
                        </div>
                        <div>
                            <span className="text-purple-400">for</span> (
                            <span className="text-purple-400">let</span> <span className="text-blue-400">i</span> = 0;
                            <span className="text-blue-400"> i</span> {'<'} <span className="text-green-400">{laps}</span>;
                            <span className="text-blue-400"> i</span>++
                            ) {'{'}
                        </div>
                        <div className={`pl-6 py-2 my-1 rounded border-l-2 transition-colors duration-300 ${isRunning ? 'bg-orange-500/10 border-orange-500' : 'border-transparent'}`}>
                            <span className="text-slate-500 italic">// Run this {laps} times</span>
                            <br />
                            <span className="text-orange-300">bananas</span>.<span className="text-yellow-400">push</span>('🍌');
                            {isRunning && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-xs text-green-400 mt-2 font-bold"
                                >
                                    Current value of i: {currentLap < 0 ? 0 : currentLap}
                                </motion.div>
                            )}
                        </div>
                        <div>{'}'}</div>
                    </div>
                </div>

                {/* RIGHT: Visual Track & Array */}
                <div className="space-y-6">
                    <div className="bg-slate-800 rounded-[3rem] p-8 h-[400px] relative overflow-hidden border-8 border-slate-900 shadow-2xl flex items-center justify-center">
                        {/* Track Pattern */}
                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
                                backgroundSize: '20px 20px'
                            }}
                        />

                        {/* Circular Track */}
                        <div className="w-64 h-64 rounded-full border-[24px] border-slate-700/50 relative shadow-inner">
                            <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/10 animate-[spin_10s_linear_infinite]" />
                        </div>

                        {/* The Runner */}
                        <motion.div
                            animate={isRunning ? { rotate: 360 } : { rotate: 0 }}
                            transition={isRunning ? { repeat: Infinity, duration: speed / 1000, ease: "linear" } : { duration: 0.5 }}
                            className="absolute w-[300px] h-[300px] pointer-events-none"
                        >
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8">
                                <img
                                    src="/assets/gorillas/gorilla_runner.png"
                                    alt="Gorilla Runner"
                                    className="w-24 h-24 object-contain transform rotate-90 filter drop-shadow-2xl"
                                />
                            </div>
                        </motion.div>

                        {/* Center Status */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center z-10">
                                <div className="text-slate-400 text-xs font-bold uppercase mb-1 tracking-widest">Completed</div>
                                <div className="text-6xl font-black text-white font-mono tabular-nums">
                                    {Math.max(0, currentLap)}
                                    <span className="text-2xl text-slate-500">/{laps}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Array Content */}
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 min-h-[120px]">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-4 flex items-center gap-2 tracking-wider">
                            <Box size={14} className="text-orange-400" /> Array Memory ['bananas']
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <AnimatePresence>
                                {bananas.map((b, i) => (
                                    <motion.div
                                        initial={{ scale: 0, rotate: -180 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        key={i}
                                        className="text-3xl bg-white/10 rounded-lg p-1 shadow-lg border border-white/5"
                                    >
                                        {b}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            {bananas.length === 0 && (
                                <span className="text-slate-500 italic text-sm">Memory empty. Start the loop to fill the array...</span>
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* Game Call-to-Action */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-16 w-full max-w-4xl mx-auto"
            >
                <Link to="/games/loop-racer" className="group block">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-1 overflow-hidden shadow-2xl hover:shadow-blue-500/30 transition-shadow">
                        <div className="bg-slate-900 rounded-[22px] p-8 flex flex-col md:flex-row items-center justify-between gap-8 group-hover:bg-slate-900/90 transition-colors">
                            <div className="flex items-center gap-6">
                                <div className="w-20 h-20 bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform duration-300">
                                    <Gamepad2 size={40} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">Ready to Race?</h2>
                                    <p className="text-slate-400 max-w-lg">
                                        Test your loop skills in the <strong>Loop Racer</strong> mini-game!
                                        Configure loops perfectly to cross the finish line without crashing.
                                    </p>
                                </div>
                            </div>
                            <div className="bg-white text-blue-900 font-bold px-8 py-3 rounded-xl flex items-center gap-2 group-hover:scale-105 transition-transform">
                                Play Loop Racer <ArrowRight size={20} />
                            </div>
                        </div>
                    </div>
                </Link>
            </motion.div>
        </div>
    );
};

const CodeIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
    </svg>
);

// ============ MAIN COMPONENT ============
export const LoopsArrays = () => {
    // Optimized selector to prevent re-renders from unrelated store changes
    const completeModule = useAcademyStore(state => state.completeModule);
    const [completed, setCompleted] = useState(false);

    const handleComplete = React.useCallback(() => {
        setCompleted(true);
        completeModule('loops-arrays', 50, 'badge_loops');
    }, [completeModule]);

    return (
        <LessonWrapper
            moduleName="Loops & Arrays"
            moduleEmoji="🔄"
            moduleColor="orange"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <LoopGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default LoopsArrays;
